import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  getPlatos,
  getCategorias,
  getMenuRango,
  cargarMenuDia,
  getSedesActivas,
  getConfiguracion,
} from '../../services/api';

function MenuMensual() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [platos, setPlatos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [platosSeleccionados, setPlatosSeleccionados] = useState([]);
  const [menusDelMes, setMenusDelMes] = useState({});
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState(null);
  const [tipoComida, setTipoComida] = useState('almuerzo');
  const [platosMinimos, setPlatosMinimos] = useState(0);
  const [platosMaximos, setPlatosMaximos] = useState(0);
  const [categoriasExpandidas, setCategoriasExpandidas] = useState({});
  const [erroresPorCategoria, setErroresPorCategoria] = useState({});

  useEffect(() => {
    cargarDatos();
    cargarSedes();
    cargarConfiguracion();
  }, []);

  useEffect(() => {
    if (sedeSeleccionada) {
      cargarMenusDelMes();
    }
  }, [selectedDate, sedeSeleccionada, tipoComida]);

  const cargarSedes = async () => {
    try {
      const res = await getSedesActivas();
      setSedes(res.data);
      // Seleccionar la primera sede por defecto si hay alguna
      if (res.data.length > 0) {
        setSedeSeleccionada(res.data[0].id);
      }
    } catch (error) {
      console.error('Error cargando sedes:', error);
      mostrarMensaje('Error al cargar sedes', 'error');
    }
  };

  const cargarDatos = async () => {
    try {
      const [platosRes, categoriasRes] = await Promise.all([
        getPlatos(),
        getCategorias(),
      ]);
      setPlatos(platosRes.data);
      setCategorias(categoriasRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      mostrarMensaje('Error al cargar datos', 'error');
    }
  };

  const cargarMenusDelMes = async () => {
    if (!sedeSeleccionada) return;

    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      const primerDia = new Date(year, month, 1);
      const ultimoDia = new Date(year, month + 1, 0);

      const fechaInicio = primerDia.toISOString().split('T')[0];
      const fechaFin = ultimoDia.toISOString().split('T')[0];

      // Cargar AMBOS tipos de comida para poder mostrar los puntos correctamente
      const [responseAlmuerzo, responseCena] = await Promise.all([
        getMenuRango(fechaInicio, fechaFin, sedeSeleccionada, 'almuerzo'),
        getMenuRango(fechaInicio, fechaFin, sedeSeleccionada, 'cena')
      ]);

      const menusObj = {};
      
      // Procesar almuerzo
      responseAlmuerzo.data.forEach((item) => {
        const fecha = item.fecha;
        if (!menusObj[fecha]) {
          menusObj[fecha] = {
            fecha: fecha,
            categorias: [],
            hasAlmuerzo: false,
            hasCena: false
          };
        }

        let categoria = menusObj[fecha].categorias.find(c => c.categoria_id === item.categoria_id && c.tipo_comida === 'almuerzo');
        if (!categoria) {
          categoria = {
            categoria_id: item.categoria_id,
            categoria: item.categoria,
            orden: item.orden,
            tipo_comida: 'almuerzo',
            platos: []
          };
          menusObj[fecha].categorias.push(categoria);
        }

        categoria.platos.push({
          menu_id: item.menu_id,
          plato_id: item.plato_id,
          plato: item.plato,
          descripcion: item.descripcion,
          imagen_url: item.imagen_url,
          disponible: item.disponible,
          cantidad_disponible: item.cantidad_disponible,
          tipo_comida: item.tipo_comida
        });

        if (item.disponible) {
          menusObj[fecha].hasAlmuerzo = true;
        }
      });

      // Procesar cena
      responseCena.data.forEach((item) => {
        const fecha = item.fecha;
        if (!menusObj[fecha]) {
          menusObj[fecha] = {
            fecha: fecha,
            categorias: [],
            hasAlmuerzo: false,
            hasCena: false
          };
        }

        let categoria = menusObj[fecha].categorias.find(c => c.categoria_id === item.categoria_id && c.tipo_comida === 'cena');
        if (!categoria) {
          categoria = {
            categoria_id: item.categoria_id,
            categoria: item.categoria,
            orden: item.orden,
            tipo_comida: 'cena',
            platos: []
          };
          menusObj[fecha].categorias.push(categoria);
        }

        categoria.platos.push({
          menu_id: item.menu_id,
          plato_id: item.plato_id,
          plato: item.plato,
          descripcion: item.descripcion,
          imagen_url: item.imagen_url,
          disponible: item.disponible,
          cantidad_disponible: item.cantidad_disponible,
          tipo_comida: item.tipo_comida
        });

        if (item.disponible) {
          menusObj[fecha].hasCena = true;
        }
      });

      // Ordenar categorías por orden
      Object.values(menusObj).forEach(menu => {
        menu.categorias.sort((a, b) => a.orden - b.orden);
      });

      setMenusDelMes(menusObj);
    } catch (error) {
      console.error('Error cargando menús del mes:', error);
    }
  };

  const cargarConfiguracion = async () => {
    try {
      const usuario = JSON.parse(sessionStorage.getItem('usuario'));
      if (!usuario) return;
      
      const response = await getConfiguracion(usuario.dni);
      const configObj = {};
      response.data.forEach(item => {
        configObj[item.clave] = item.valor;
      });
      
      setPlatosMinimos(Number.parseInt(configObj.platos_minimos) || 0);
      setPlatosMaximos(Number.parseInt(configObj.platos_maximos) || 0);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      // Valores por defecto si falla
      setPlatosMinimos(0);
      setPlatosMaximos(0);
    }
  };

  const togglePlato = (platoId) => {
    setPlatosSeleccionados((prev) => {
      if (prev.includes(platoId)) {
        return prev.filter((id) => id !== platoId);
      }
      return [...prev, platoId];
    });
    
    // Limpiar error de esta categoría al seleccionar/deseleccionar
    const plato = platos.find((p) => p.id === platoId);
    if (plato) {
      setErroresPorCategoria((prev) => {
        const newErrors = { ...prev };
        delete newErrors[plato.categoria_id];
        return newErrors;
      });
    }
  };

  const toggleCategoria = (categoriaId) => {
    setCategoriasExpandidas((prev) => ({
      ...prev,
      [categoriaId]: !prev[categoriaId]
    }));
  };

  const handleFechaChange = (nuevaFecha) => {
    setSelectedDate(nuevaFecha);
    setPlatosSeleccionados([]); // Limpiar selección al cambiar de fecha
    setErroresPorCategoria({}); // Limpiar errores también
  };

  const validarSeleccion = () => {
    const errores = [];
    const erroresCat = {};
    const platosPorCategoria = {};
    
    categorias.forEach((cat) => {
      platosPorCategoria[cat.id] = 0;
    });

    platosSeleccionados.forEach((platoId) => {
      const plato = platos.find((p) => p.id === platoId);
      if (plato) {
        platosPorCategoria[plato.categoria_id]++;
      }
    });

    // Validar según configuración
    categorias.forEach((cat) => {
      // Contar platos activos disponibles en esta categoría
      const platosDisponibles = platos.filter((p) => p.categoria_id === cat.id && p.activo).length;
      
      // Solo validar si la categoría tiene platos disponibles
      if (platosDisponibles === 0) {
        return; // Saltar validación para categorías vacías
      }

      const cantidad = platosPorCategoria[cat.id] || 0;

      if (platosMinimos > 0 && cantidad < platosMinimos) {
        const mensaje = `Debes seleccionar al menos ${platosMinimos} opción(es)`;
        errores.push(`${cat.nombre}: ${mensaje}`);
        erroresCat[cat.id] = mensaje;
      }

      if (platosMaximos > 0 && cantidad > platosMaximos) {
        const mensaje = `No puedes seleccionar más de ${platosMaximos} opción(es)`;
        errores.push(`${cat.nombre}: ${mensaje}`);
        erroresCat[cat.id] = mensaje;
      }
    });

    setErroresPorCategoria(erroresCat);
    return errores;
  };

  const guardarMenuDia = async () => {
    if (!sedeSeleccionada) {
      mostrarMensaje('Debes seleccionar una sede', 'error');
      return;
    }

    const errores = validarSeleccion();
    if (errores.length > 0) {
      mostrarMensaje(errores.join('. '), 'error');
      return;
    }

    setLoading(true);
    try {
      const fecha = selectedDate.toISOString().split('T')[0];
      const platos = platosSeleccionados.map((plato_id) => ({
        plato_id,
        disponible: true,
      }));

      const payload = { fecha, platos, sede_id: sedeSeleccionada, tipo_comida: tipoComida };
      console.log('📤 Enviando al backend:', payload);

      await cargarMenuDia(payload);
      mostrarMensaje(`Menú de ${tipoComida} guardado exitosamente`, 'success');
      setPlatosSeleccionados([]);
      setErroresPorCategoria({});
      cargarMenusDelMes();
    } catch (error) {
      console.error('Error guardando menú:', error);
      mostrarMensaje(
        error.response?.data?.error || 'Error al guardar menú',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const cargarMenuExistente = () => {
    const fecha = selectedDate.toISOString().split('T')[0];
    const menuExistente = menusDelMes[fecha];

    if (menuExistente && menuExistente.categorias && Array.isArray(menuExistente.categorias)) {
      const platosIds = [];
      menuExistente.categorias.forEach((cat) => {
        if (cat.platos && Array.isArray(cat.platos)) {
          cat.platos.forEach((plato) => {
            platosIds.push(plato.plato_id);
          });
        }
      });
      setPlatosSeleccionados(platosIds);
      mostrarMensaje('Menú cargado para edición', 'info');
    } else {
      console.log('No hay menú existente para esta fecha o estructura incorrecta:', menuExistente);
    }
  };

  const mostrarMensaje = (texto, tipo) => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 5000);
  };

  const tileClassName = ({ date }) => {
    const fechaStr = date.toISOString().split('T')[0];
    if (menusDelMes[fechaStr]) {
      return 'react-calendar__tile--hasMenu';
    }
    return null;
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const fechaStr = date.toISOString().split('T')[0];
    const menu = menusDelMes[fechaStr];
    if (!menu) return null;

    return (
      <div className="tile-dots" aria-hidden>
        {menu.hasAlmuerzo && <span className="dot dot--almuerzo" />}
        {menu.hasCena && <span className="dot dot--cena" />}
      </div>
    );
  };

  const platosPorCategoria = (categorias || []).map((cat) => ({
    ...cat,
    platos: (platos || []).filter((p) => p.categoria_id === cat.id && p.activo),
  }));

  const fechaSeleccionada = selectedDate.toISOString().split('T')[0];
  const tieneMenuExistente = menusDelMes[fechaSeleccionada];

  return (
    <div>
      <div className="page-header">
        <h1>Gestión de Menú Mensual</h1>
        <p>Carga y administra el menú para cada día del mes</p>
      </div>

      {/* Selector de Sede y Tipo de Comida */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="sede-selector">Sede</label>
              <select
                id="sede-selector"
                className="form-control"
                value={sedeSeleccionada || ''}
                onChange={(e) => {
                  setSedeSeleccionada(Number(e.target.value));
                  setPlatosSeleccionados([]);
                }}
              >
                <option value="">Selecciona una sede</option>
                {sedes.map((sede) => (
                  <option key={sede.id} value={sede.id}>
                    {sede.nombre} ({sede.codigo})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="tipo-comida-selector">Tipo de Comida</label>
              <select
                id="tipo-comida-selector"
                className="form-control"
                value={tipoComida}
                onChange={(e) => {
                  setTipoComida(e.target.value);
                  setPlatosSeleccionados([]);
                }}
              >
                <option value="almuerzo">Almuerzo</option>
                <option value="cena">Cena</option>
              </select>
            </div>
          </div>
        </div>
        {sedeSeleccionada && (
          <small className="text-muted" style={{ marginTop: '10px', display: 'block' }}>
            Estás configurando el menú de <strong>{tipoComida}</strong> para:{' '}
            <strong>{sedes.find((s) => s.id === sedeSeleccionada)?.nombre}</strong>
          </small>
        )}
      </div>

      <div className="card">
        <h2>Calendario</h2>
        <div style={{ width: '100%' }}>
          <Calendar
            onChange={handleFechaChange}
            value={selectedDate}
            tileClassName={tileClassName}
            tileContent={tileContent}
            locale="es-ES"
            className="calendar-full-width"
          />
        </div>
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <p>
            Fecha seleccionada: <strong>{fechaSeleccionada}</strong>
          </p>
          {tieneMenuExistente && (
            <button
              className="btn btn-secondary"
              onClick={cargarMenuExistente}
              style={{ marginTop: '10px' }}
            >
              Editar menú existente
            </button>
          )}

          {/* Leyenda de puntos */}
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#27AE60', display: 'inline-block' }}></span>
              <small>Almuerzo</small>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FB8C00', display: 'inline-block' }}></span>
              <small>Cena</small>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2>Seleccionar Platos de {tipoComida.charAt(0).toUpperCase() + tipoComida.slice(1)} para {fechaSeleccionada}</h2>
          <div>
            <button
              className="btn btn-secondary"
              onClick={() => setPlatosSeleccionados([])}
              style={{ marginRight: '10px' }}
            >
              Limpiar
            </button>
            <button
              className="btn btn-success"
              onClick={guardarMenuDia}
              disabled={loading || platosSeleccionados.length === 0}
            >
              {loading ? 'Guardando...' : 'Guardar Menú'}
            </button>
          </div>
        </div>

        {mensaje && (
          <div className={`alert alert-${mensaje.tipo}`} style={{ marginTop: '15px' }}>{mensaje.texto}</div>
        )}

        <p style={{ marginTop: '10px', color: '#7f8c8d' }}>
          Seleccionados: {platosSeleccionados.length} platos
        </p>

        {platosPorCategoria.map((categoria) => {
          const isExpanded = categoriasExpandidas[categoria.id] !== false; // Expandido por defecto
          const categoriaTieneError = erroresPorCategoria[categoria.id];
          const cantidadSeleccionada = categoria.platos.filter((p) => platosSeleccionados.includes(p.id)).length;
          
          return (
            <div 
              key={categoria.id} 
              style={{ 
                marginTop: '20px',
                border: categoriaTieneError ? '2px solid #e74c3c' : '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <div 
                role="button"
                tabIndex={0}
                onClick={() => toggleCategoria(categoria.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleCategoria(categoria.id); }}
                style={{
                  padding: '15px 20px',
                  background: categoriaTieneError ? '#fee' : '#f8f9fa',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none'
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: '#2c3e50', display: 'inline-block' }}>
                    {isExpanded ? '▼' : '▶'} {categoria.nombre}
                  </h3>
                  <span style={{ fontSize: '14px', color: '#7f8c8d', marginLeft: '10px' }}>
                    ({cantidadSeleccionada} seleccionados
                    {platosMinimos > 0 && ` / mínimo ${platosMinimos}`}
                    {platosMaximos > 0 && ` / máximo ${platosMaximos}`})
                  </span>
                </div>
                {categoriaTieneError && (
                  <span style={{ color: '#e74c3c', fontSize: '14px', fontWeight: 'bold' }}>
                    ⚠️ Error
                  </span>
                )}
              </div>

              {isExpanded && (
                <div style={{ padding: '20px' }}>
                  {categoriaTieneError && (
                    <div 
                      className="alert alert-danger" 
                      style={{ 
                        marginBottom: '15px',
                        padding: '12px',
                        borderRadius: '4px',
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        border: '1px solid #f5c6cb'
                      }}
                    >
                      {erroresPorCategoria[categoria.id]}
                    </div>
                  )}
                  <div className="menu-grid">
                    {categoria.platos.map((plato) => (
                      <div
                        key={plato.id}
                        role="button"
                        tabIndex={0}
                        className={`menu-item ${
                          platosSeleccionados.includes(plato.id) ? 'selected' : ''
                        }`}
                        onClick={() => togglePlato(plato.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') togglePlato(plato.id); }}
                      >
                        {plato.imagen_url && (
                          <img src={plato.imagen_url} alt={plato.nombre} />
                        )}
                        <h4>{plato.nombre}</h4>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#7f8c8d',
                            marginTop: '5px',
                          }}
                        >
                          {plato.descripcion}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MenuMensual;
